import type { CompatibilityDefinition, RouteStatus, RuleSeverity, Scenario, TrafficLevel, ValidityRuleDefinition } from 'fuel-benchmark-contracts';
import {
  hvacStateSchema,
  routeTypeSchema,
  ruleSeveritySchema,
  seasonSchema,
  testTypeSchema,
  timeBandSchema,
  trafficLevelSchema,
} from 'fuel-benchmark-contracts';
import type { TFunction } from 'i18next';
import type { StatusTone } from '../../components/ui/StatusText';

export const TEST_TYPES = testTypeSchema.options;
export const SEASONS = seasonSchema.options;
export const TIME_BANDS = timeBandSchema.options;
export const HVAC_STATES = hvacStateSchema.options;
export const ROUTE_TYPES = routeTypeSchema.options;
export const SCENARIO_TRAFFIC_LEVELS = trafficLevelSchema.options;

/**
 * `High traffic` — the traffic segment of a scenario's condition summary, or `null` when the scenario
 * prescribes none. Shared by every screen that summarizes a scenario, so the wording cannot drift.
 */
export const trafficSummary = (trafficLevel: TrafficLevel | null, t: TFunction): string | null =>
  trafficLevel === null ? null : t('conditions.scenarios.trafficSummary', { level: t(`enums.trafficLevel.${trafficLevel}`) });

/**
 * `Summer · Day · A/C on · Urban · High traffic` / `Summer · Day · A/C off · Idle 30 min` — the one-line
 * condition summary the scenario table and the report share (plan §7). Traffic joins it exactly when the
 * scenario prescribes one; each run still records the traffic it actually met.
 */
export const scenarioConditions = (scenario: Scenario, t: TFunction): string => {
  const tail =
    scenario.testType === 'IDLE'
      ? `${t('enums.testType.IDLE')} ${scenario.idleDurationMin ?? '?'} min`
      : scenario.routeType === null
        ? t('common.notSet')
        : t(`enums.routeType.${scenario.routeType}`);

  return [
    t(`enums.season.${scenario.season}`),
    t(`enums.timeBand.${scenario.timeBand}`),
    t(`enums.hvacState.${scenario.hvacState}`),
    tail,
    trafficSummary(scenario.trafficLevel, t),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
};

/** INACTIVE is the state a reader has to notice; ACTIVE is the expected one and stays muted. */
export const catalogueStatusTone = (status: 'ACTIVE' | 'INACTIVE'): StatusTone => (status === 'INACTIVE' ? 'warning' : 'muted');

/** SUPERSEDED revisions still exist for historical runs, so they are informational rather than alarming. */
export const routeStatusTone = (status: RouteStatus): StatusTone => (status === 'SUPERSEDED' ? 'warning' : 'muted');

/** A core scenario is the notable one — it is what the coverage matrix expects every vehicle to have. */
export const coreTone = (isCore: boolean): StatusTone => (isCore ? 'info' : 'muted');

export const RULE_SEVERITIES = ruleSeveritySchema.options;

/**
 * A FAIL rule is what actually invalidates a run, so it is the one severity worth colouring; a WARN rule only
 * annotates (which is what every temperature rule does by decision — plan §12.3).
 */
export const severityTone = (severity: RuleSeverity): StatusTone => (severity === 'FAIL' ? 'danger' : 'warning');

/** `measurements.ambient_temp_avg_c between 28…42 °C · SUMMER` — the rule table's one-line summary. */
export const describeRuleDefinition = (definition: ValidityRuleDefinition): string => {
  const unit = definition.unit ? ` ${definition.unit}` : '';

  const test = (() => {
    switch (definition.operator) {
      case 'between':
        return `between ${definition.min ?? '?'}…${definition.max ?? '?'}${unit}`;
      case 'lte':
        return `≤ ${String(definition.value ?? '?')}${unit}`;
      case 'gte':
        return `≥ ${String(definition.value ?? '?')}${unit}`;
      case 'equal':
        return `= ${String(definition.value ?? '?')}`;
      case 'equal_field':
        return `= ${definition.reference ?? '?'}`;
      case 'in':
        return `in ${(definition.values ?? []).join(', ')}`;
      case 'not_null':
        return 'recorded';
      case 'min_pct_of':
        return `≥ ${String(definition.value ?? '?')}% of ${definition.reference ?? '?'}`;
    }
  })();

  const scope = Object.values(definition.appliesTo ?? {})
    .flatMap((values) => values ?? [])
    .join(', ');

  return `${definition.field} ${test}${scope === '' ? '' : ` · ${scope}`}`;
};

/** `max_diff 5 C → WARN` — the compatibility table's one-line summary. */
export const describeCompatibilityDefinition = (definition: CompatibilityDefinition): string => {
  const tolerance = definition.value === undefined ? '' : ` ${definition.value}${definition.unit ? ` ${definition.unit}` : ''}`;

  return `${definition.operator}${tolerance} → ${definition.onFail}`;
};

/** Pretty-prints a definition for the JSON editor; two spaces keeps a rule readable in a narrow sheet. */
export const toDefinitionJson = (definition: unknown): string => JSON.stringify(definition, null, 2);

/**
 * A route's vertical profile — `↑ 1,330 m over 26 km (5.1%) · ↓ 1,330 m over 26 km (−5.1%)` — is written
 * server-side, once, and arrives on the route as `elevationProfile`. There is deliberately no formatter here:
 * this screen, a run's route panel and the printed report all read that one string, so they cannot phrase the
 * same survey three different ways.
 */
